/**
 * Smart Workout Hook - Uses ML-integrated workout generator
 * 
 * This hook replaces useUserWorkout with a smarter version that:
 * 1. Checks if training is advisable (recovery, ACWR)
 * 2. Generates ML-optimized workouts
 * 3. Auto-deloads when needed
 * 4. Enforces recovery thresholds
 */

import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { useApp } from '../AppContext';
import { getUserProgramSelection } from '../programManagement';
import { getCachedProgram } from '../programCache';
import { 
  generateSmartWorkout, 
  isTrainingAdvisable,
  getTodayTrainingSummary,
  type SmartWorkoutResult,
} from '../smartWorkoutGenerator';
import type { SubstitutionResult } from '../exerciseSubstitution';
import type { WorkoutProgram } from '../types/program';
import type { GeneratedExercise } from '../workoutGenerator';
import type { AdaptivePrescription, OverlayMutation } from '../adaptivePrescription';

export interface UseSmartWorkoutResult {
  // Core data
  exercises: GeneratedExercise[] | null;
  program: WorkoutProgram | null;
  
  // Status
  loading: boolean;
  error: string | null;
  
  // Smart features
  status: SmartWorkoutResult['status'] | null;
  intelligence: SmartWorkoutResult['intelligence'] | null;
  adjustments: SmartWorkoutResult['adjustments'];
  synthesisContext: SmartWorkoutResult['synthesisContext'];
  substitutions: SubstitutionResult[];
  reasoning: string[];
  message: string;
  
  // Training advisory
  isAdvisable: boolean;
  recommendation: 'train' | 'light' | 'deload' | 'rest';

  // Adaptive Personal Model (Module 4 integration)
  adaptivePrescription: AdaptivePrescription | null;
  adaptiveMutations: OverlayMutation[];
  /** Snapshot of the workout BEFORE the adaptive overlay was applied —
   *  used to revert when the user taps Override. Set only when an
   *  overlay was actually applied. */
  exercisesPreAdaptive: GeneratedExercise[] | null;

  // Actions
  refreshWorkout: () => void;
  overrideDeload: () => void;
  /** "Train anyway" on a recommended-rest day. SEPARATE intent from
   *  overrideDeload — regenerates the workout with the rest gate bypassed
   *  and the day routed through the deload-priority cap (lightened
   *  session), instead of silently re-blocking. */
  overrideRest: () => void;
  /** Revert the adaptive overlay — restore exercises to their pre-
   *  adaptive state. No-op when no overlay was applied. */
  overrideAdaptive: () => void;
}

/**
 * Smart workout hook - integrates all ML systems
 */
export function useSmartWorkout(
  week?: number,
  day?: number,
  initialOverrideDeload: boolean = false
): UseSmartWorkoutResult {
  const { user } = useAuth();
  const { oneRMs } = useApp();

  // Core state
  const [exercises, setExercises] = useState<GeneratedExercise[] | null>(null);
  const [program, setProgram] = useState<WorkoutProgram | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Smart state
  const [status, setStatus] = useState<SmartWorkoutResult['status'] | null>(null);
  const [intelligence, setIntelligence] = useState<SmartWorkoutResult['intelligence'] | null>(null);
  const [adjustments, setAdjustments] = useState<SmartWorkoutResult['adjustments']>([]);
  const [synthesisContext, setSynthesisContext] = useState<SmartWorkoutResult['synthesisContext']>(undefined);
  const [substitutions, setSubstitutions] = useState<SubstitutionResult[]>([]);
  const [reasoning, setReasoning] = useState<string[]>([]);
  const [message, setMessage] = useState('');

  // Training advisory
  const [isAdvisable, setIsAdvisable] = useState(true);
  const [recommendation, setRecommendation] = useState<'train' | 'light' | 'deload' | 'rest'>('train');

  // Adaptive Personal Model state — set after Module 1/2/3 run on top
  // of the smartGen output. exercisesPreAdaptive holds a snapshot of
  // the workout BEFORE the overlay so we can revert on Override.
  const [adaptivePrescription, setAdaptivePrescription] = useState<AdaptivePrescription | null>(null);
  const [adaptiveMutations, setAdaptiveMutations] = useState<OverlayMutation[]>([]);
  const [exercisesPreAdaptive, setExercisesPreAdaptive] = useState<GeneratedExercise[] | null>(null);

  // Override state - initialize with URL param if provided
  const [overrideDeloadFlag, setOverrideDeloadFlag] = useState(initialOverrideDeload);
  // Rest-day "train anyway" — separate intent from the deload override.
  const [overrideRestFlag, setOverrideRestFlag] = useState(false);
  
  // Prevent duplicate loads
  const lastLoadedRef = useRef<{ week: number; day: number; programId: string } | null>(null);
  const loadingRef = useRef(false);

  // Load workout function
  async function loadWorkout() {
    if (loadingRef.current) return;
    if (!user) {
      setError('User not authenticated');
      setLoading(false);
      return;
    }

    if (!week || !day) {
      setError('Week and day are required');
      setLoading(false);
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      setError(null);

      // 1. Get user's program selection
      const selection = await getUserProgramSelection(user.uid);
      if (!selection) {
        setError('No program selected. Please select a program from onboarding.');
        setLoading(false);
        loadingRef.current = false;
        return;
      }

      console.log(` Smart Workout: Loading ${selection.programId} Week ${week}, Day ${day}`);

      // Skip if same workout already loaded (unless overriding)
      if (
        lastLoadedRef.current &&
        lastLoadedRef.current.week === week &&
        lastLoadedRef.current.day === day &&
        lastLoadedRef.current.programId === selection.programId &&
        !overrideDeloadFlag &&
        !overrideRestFlag
      ) {
        console.log('⏭ useSmartWorkout: Skipping reload - same params');
        setLoading(false);
        loadingRef.current = false;
        return;
      }

      // 2. Check training advisability FIRST
      const advisory = await isTrainingAdvisable();
      setIsAdvisable(advisory.advisable);
      setRecommendation(advisory.recommendation);

      // 3. Load program
      const loadedProgram = await getCachedProgram(selection.programId, user.uid);
      if (!loadedProgram) {
        setError(`Program ${selection.programId} not found`);
        setLoading(false);
        loadingRef.current = false;
        return;
      }

      setProgram(loadedProgram);

      // 4. Generate SMART workout (with all ML integration)
      console.log(` Generating smart workout...`);
      const result = await generateSmartWorkout(
        loadedProgram,
        week,
        day,
        oneRMs,
        {
          overrideDeload: overrideDeloadFlag,
          overrideRest: overrideRestFlag,
        }
      );

      // 4b. Adaptive Personal Model overlay (Module 4 integration)
      // Computes PersonalState + PatternMemory + AdaptivePrescription
      // on top of the smartGen output. If a non-trivial adjustment is
      // recommended (and confidence ≥30%), mutates result.workout in
      // place by applying loadMultiplier / dropTopSets / restExtension
      // / swapTo per exercise. The original workout is snapshotted to
      // exercisesPreAdaptive so Override can revert.
      let preAdaptiveSnapshot: GeneratedExercise[] | null = null;
      let appliedPrescription: AdaptivePrescription | null = null;
      let appliedMutations: OverlayMutation[] = [];
      if (result.workout && result.workout.length > 0 && result.status !== 'rest' && result.status !== 'recommended-rest' && result.status !== 'program-complete') {
        try {
          const { loadPersonalState } = await import('../personalState');
          const { loadPatternMemory, predictTodayQuality } = await import('../patternMemory');
          const { computeAdaptivePrescription, categorizeExercise, applyPrescriptionOverlay, routeOverlayThroughSessionDecision } = await import('../adaptivePrescription');

          // Best-effort age lookup
          let ageYears: number | null = null;
          try {
            const { doc, getDoc } = await import('firebase/firestore');
            const { db } = await import('../firebase');
            const snap = await getDoc(doc(db, 'users', user.uid));
            const userData = snap.exists() ? snap.data() : null;
            if (typeof userData?.age === 'number' && userData.age > 0) ageYears = userData.age;
          } catch {}

          const [state, memory] = await Promise.all([
            loadPersonalState(user.uid, ageYears),
            loadPatternMemory(user.uid, ageYears),
          ]);
          const prediction = predictTodayQuality(state, memory);

          // Adapt smart-gen workout into PrescribedExercise[] for Module 3
          const prescribedExercises = result.workout.map((ex, idx) => ({
            name: ex.name,
            category: categorizeExercise(ex.name, ex.block),
            prescribedSets: (() => {
              const m = ex.sets_reps?.match(/^(\d+)/);
              return m ? parseInt(m[1], 10) : 3;
            })(),
            // Primary = first compound-heavy or compound-light exercise
            isPrimary: idx === result.workout!.findIndex(e => {
              const c = categorizeExercise(e.name, e.block);
              return c === 'compound-heavy' || c === 'compound-light';
            }),
          }));

          const rawPrescription = computeAdaptivePrescription({
            state,
            memory,
            prediction,
            prescribedExercises,
          });

          // Route the overlay through the generator's decision-table semantics
          // (audit P0-4): the session-level intensity cap and the overlay's
          // morning-state cap are the same signal family, so the TIGHTER one
          // binds — they never stack — and the 60% safety floor is re-applied
          // to the combined result. Without this, a deload (×0.70) plus a
          // 'recover' overlay (×0.70) silently produced 49% loads outside
          // every floor — the exact multi-apply bug the table was built to kill.
          const prescription = routeOverlayThroughSessionDecision(
            rawPrescription,
            result.intelligence?.intensityAdjustment ?? 1.0,
            result.intelligence?.volumeAdjustment ?? 1.0,
          );

          // Apply overlay only when there's a real adjustment to make
          if (prescription.severity !== 'none' && prescription.confidence >= 0.30) {
            // Deep-clone the workout BEFORE mutation so Override can revert
            preAdaptiveSnapshot = JSON.parse(JSON.stringify(result.workout));
            appliedMutations = applyPrescriptionOverlay(result.workout, prescription);
            appliedPrescription = prescription;
            if (appliedMutations.length === 0) {
              // Fully suppressed by the session cap — nothing changed, so
              // don't offer a revert for a no-op.
              preAdaptiveSnapshot = null;
            }
            console.log(`  Adaptive overlay applied: ${prescription.severity} severity, ${appliedMutations.length} exercises mutated`);
          } else {
            // Track the prescription even when no overlay applied — UI may
            // surface "no adjustment" framing.
            appliedPrescription = prescription;
          }
        } catch (apmErr) {
          console.warn('[smart-workout] adaptive overlay failed (non-critical):', apmErr);
        }
      }

      // 5. Update all state
      setExercises(result.workout);
      setStatus(result.status);
      setIntelligence(result.intelligence);
      setAdjustments(result.adjustments);
      setSynthesisContext(result.synthesisContext);
      setSubstitutions(result.substitutions);
      setReasoning(result.reasoning);
      setMessage(result.message);
      setAdaptivePrescription(appliedPrescription);
      setAdaptiveMutations(appliedMutations);
      setExercisesPreAdaptive(preAdaptiveSnapshot);

      // Log summary
      console.log(` Smart Workout Result:`, {
        status: result.status,
        exercises: result.workout?.length || 0,
        adjustments: result.adjustments.length,
        deload: result.intelligence.deloadTriggered,
        acwr: result.intelligence.acwr.toFixed(2),
        recovery: result.intelligence.recoveryScore,
      });

      // Mark as loaded
      lastLoadedRef.current = { week, day, programId: selection.programId };
      
    } catch (err) {
      console.error(' Smart workout error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load workout');
    } finally {
      setLoading(false);
      loadingRef.current = false;
      setOverrideDeloadFlag(false); // Reset override after use
      setOverrideRestFlag(false);   // Reset rest override after use
    }
  }

  // Load on mount and when params change
  useEffect(() => {
    loadWorkout();
  }, [user, week, day, oneRMs, overrideDeloadFlag, overrideRestFlag]);

  // Actions
  const refreshWorkout = () => {
    lastLoadedRef.current = null; // Force reload
    loadWorkout();
  };

  const overrideDeload = () => {
    console.log(' User overriding deload recommendation');
    setOverrideDeloadFlag(true);
    lastLoadedRef.current = null; // Force reload
  };

  const overrideRest = () => {
    console.log(' User training through recommended rest — regenerating as a lightened (deload-cap) session');
    setOverrideRestFlag(true);
    lastLoadedRef.current = null; // Force reload
  };

  /**
   * Override the adaptive prescription — restore exercises to the
   * snapshot taken before the overlay was applied. Caller's session
   * state is reset to the original prescription.
   *
   * Side-effect: logs the override decision to Firestore for future
   * learning. The system can then weight predictions less aggressively
   * if the user consistently overrides on certain stream patterns.
   */
  const overrideAdaptive = () => {
    if (!exercisesPreAdaptive) return;
    console.log(' User overriding adaptive prescription');
    setExercises(exercisesPreAdaptive);
    setAdaptiveMutations([]);
    // Persist override telemetry — best-effort, non-blocking
    if (user?.uid && adaptivePrescription) {
      (async () => {
        try {
          const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
          const { db } = await import('../firebase');
          const id = `${Date.now()}`;
          await setDoc(doc(db, 'users', user.uid, 'adaptiveOverrides', id), {
            timestamp: serverTimestamp(),
            severity: adaptivePrescription.severity,
            predictedQualityWithout: adaptivePrescription.predictedQualityWithoutAdjustment,
            predictedQualityWith: adaptivePrescription.predictedQualityWithAdjustment,
            confidence: adaptivePrescription.confidence,
            triggers: adaptivePrescription.triggers.map(t => ({ stream: t.stream, state: t.state })),
            week, day,
          });
        } catch (e) {
          console.warn('[smart-workout] override telemetry failed:', e);
        }
      })();
    }
  };

  return {
    exercises,
    program,
    loading,
    error,
    status,
    intelligence,
    adjustments,
    synthesisContext,
    substitutions,
    reasoning,
    message,
    isAdvisable,
    recommendation,
    adaptivePrescription,
    adaptiveMutations,
    exercisesPreAdaptive,
    refreshWorkout,
    overrideDeload,
    overrideRest,
    overrideAdaptive,
  };
}

/**
 * Quick hook for dashboard - just checks training status
 */
export function useTrainingAdvisory() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<{
    recommendation: 'train' | 'light' | 'deload' | 'rest';
    title: string;
    subtitle: string;
    color: 'green' | 'yellow' | 'orange' | 'red';
    metrics: {
      recoveryScore: number;
      acwr: number;
      consecutiveDays: number;
    };
  } | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const result = await getTodayTrainingSummary();
        setSummary(result);
      } catch (error) {
        console.error('Error loading training advisory:', error);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, []);

  return { loading, summary };
}

